import { useEffect, useState } from "react";
import { useUser } from "context/UserContext";
import { useNavigate, Link } from "react-router-dom";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MuiLink from "@mui/material/Link";

import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import MDInput from "mdComponents/MDInput";
import MDButton from "mdComponents/MDButton";

import BasicLayoutNew from "../components/BasicLayoutNew";
import LoadingSpinner from "controls/LoadingSpinner";
import { MainLogo } from "components/Icons/MainLogo";
import { APP_NAME, MESSAGE_AUTH } from "constants";
import bgImage from "assets/images/city.jpg";
import phoneMapImage from "assets/images/nexus-6-singleMap.png";
import {
  authFeatureChipSx,
  authInputSx,
  authPanelSx,
  authSurfaceSx,
  primaryButtonSx,
  secondaryButtonSx,
} from "../authStyles";

function SignIn() {
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, email, setEmail, password, setPassword, isLoggedIn, forgotPassword } = useUser();
  const navigate = useNavigate();

  const handleLogin = async () => {
    setErrorMessage("");
    setLoading(true);
    try {
      const { error } = await login({ email, password });
      if (error) {
        if (error.toString().indexOf("Email not confirmed") > -1) {
          setErrorMessage(MESSAGE_AUTH.LOGIN_INFO_EMAIL_NOT_CONFIRMED);
        } else {
          setErrorMessage(MESSAGE_AUTH.LOGIN_INFO_INCORRECT);
        }
      }
    } catch (err) {
      setErrorMessage("Login failed. Please check your credentials and try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setErrorMessage("");
    if (!email || email.toString().trim() === "") {
      setErrorMessage(MESSAGE_AUTH.FORGOT_PASSWORD_EMPTY_EMAIL);
      return;
    }

    setSendingEmail(true);
    if (await forgotPassword(email)) {
      setErrorMessage(MESSAGE_AUTH.FORGOT_PASSWORD_EMAIL_SENT);
    } else {
      setErrorMessage(MESSAGE_AUTH.FORGOT_PASSWORD_EMAIL_SENT_ERROR);
    }
    setSendingEmail(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!loading) handleLogin();
  };

  useEffect(() => {
    if (isLoggedIn) navigate("/dashboard");
  }, [isLoggedIn, navigate]);

  return (
    <BasicLayoutNew image={bgImage} wide>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item lg={5} xs={12} md={12} xl={4}>
          <Card sx={{ ...authSurfaceSx, height: "100%" }}>
            <MDBox p={{ xs: 3, md: 4 }}>
              <MDBox textAlign="center">
                <MDBox
                  sx={{
                    minHeight: 116,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MainLogo style={{ width: "220px", maxWidth: "78%" }} />
                </MDBox>
                <MDTypography variant="h3" fontWeight={900} color="dark" mt={1}>
                  Welcome back
                </MDTypography>
                <MDTypography variant="body2" color="text" fontWeight="medium" mt={1}>
                  Sign in to manage locations, visits, ratings, and host operations.
                </MDTypography>
              </MDBox>

              <MDBox component="form" role="form" mt={3} onSubmit={handleSubmit}>
                <MDBox mb={1.5}>
                  <MDInput
                    type="email"
                    label="Email"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={authInputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>

                <MDBox mb={1}>
                  <MDInput
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={authInputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            edge="end"
                            size="small"
                            onClick={() => setShowPassword((value) => !value)}
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon fontSize="small" />
                            ) : (
                              <VisibilityOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>

                <MDBox display="flex" justifyContent="flex-end" minHeight={34}>
                  <MDButton
                    variant="text"
                    color="info"
                    onClick={handleForgotPassword}
                    disabled={sendingEmail}
                    sx={{ px: 0.25, fontWeight: 900, textTransform: "none" }}
                  >
                    {sendingEmail ? "Sending..." : "Forgot password?"}
                  </MDButton>
                </MDBox>

                {errorMessage && !loading && (
                  <MDBox
                    mt={1}
                    p={1.5}
                    borderRadius="12px"
                    sx={{
                      backgroundColor:
                        errorMessage === MESSAGE_AUTH.FORGOT_PASSWORD_EMAIL_SENT
                          ? "rgba(46, 174, 31, 0.10)"
                          : "rgba(215, 38, 61, 0.10)",
                    }}
                  >
                    <MDTypography
                      variant="body2"
                      color={errorMessage === MESSAGE_AUTH.FORGOT_PASSWORD_EMAIL_SENT ? "success" : "error"}
                      textAlign="center"
                      fontWeight="medium"
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      {errorMessage}
                    </MDTypography>
                  </MDBox>
                )}

                {loading ? (
                  <MDBox mt={2}>
                    <LoadingSpinner size={25} title="Signing you in..." />
                  </MDBox>
                ) : (
                  <>
                    <MDBox mt={2}>
                      <MDButton
                        type="submit"
                        variant="gradient"
                        color="info"
                        fullWidth
                        sx={primaryButtonSx}
                        startIcon={<LoginOutlinedIcon />}
                      >
                        Log In
                      </MDButton>
                    </MDBox>

                    <MDBox mt={1.25}>
                      <MDButton
                        component={Link}
                        to="/authentication/sign-up"
                        variant="outlined"
                        color="dark"
                        fullWidth
                        sx={secondaryButtonSx}
                        startIcon={<StorefrontOutlinedIcon />}
                      >
                        Register Your Business
                      </MDButton>
                    </MDBox>

                    <MDBox mt={2.25} textAlign="center">
                      <MDTypography variant="button" color="text" fontWeight="medium">
                        Looking for a personal account?{" "}
                        <MDTypography
                          component={MuiLink}
                          href="https://pappasquat.com"
                          variant="button"
                          color="info"
                          fontWeight="bold"
                          textGradient
                        >
                          Download the app
                        </MDTypography>
                      </MDTypography>
                    </MDBox>
                  </>
                )}
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item lg={7} xs={12} md={12} xl={8}>
          <Card sx={{ ...authPanelSx, height: "100%" }}>
            <MDBox
              sx={{
                p: { xs: 3, md: 5 },
                minHeight: "100%",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                gap: { xs: 3, md: 5 },
              }}
            >
              <MDBox flex={1}>
                <MDBox sx={authFeatureChipSx}>
                  <InsightsOutlinedIcon sx={{ fontSize: 18 }} />
                  Host Portal
                </MDBox>
                <MDTypography
                  variant="h2"
                  fontWeight={900}
                  mt={2}
                  mb={1.5}
                  sx={{ color: "#ffffff", lineHeight: 1.05 }}
                >
                  Turn your restroom into an asset.
                </MDTypography>
                <MDTypography
                  variant="body1"
                  sx={{ color: "rgba(248, 251, 255, 0.78)", fontWeight: 500, lineHeight: 1.7 }}
                >
                  {APP_NAME} helps businesses turn restroom access into discoverability,
                  foot traffic, cleaner operations, and useful visit insights.
                </MDTypography>

                <Grid container spacing={1.5} mt={2}>
                  {["Increase foot traffic", "Control access", "Track usage", "Improve ratings"].map(
                    (item) => (
                      <Grid item xs={12} sm={6} key={item}>
                        <MDBox
                          sx={{
                            p: 1.5,
                            borderRadius: "15px",
                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                            border: "1px solid rgba(255, 255, 255, 0.10)",
                          }}
                        >
                          <MDTypography variant="button" fontWeight="bold" sx={{ color: "#ffffff" }}>
                            {item}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    )
                  )}
                </Grid>

                <MDBox display="flex" gap={1.5} mt={3} flexWrap="wrap">
                  <MDButton
                    component={Link}
                    to="/authentication/sign-up"
                    variant="gradient"
                    color="info"
                    sx={primaryButtonSx}
                  >
                    Get Started
                  </MDButton>
                  <MDButton
                    component={Link}
                    to="/authentication/learn-more"
                    variant="outlined"
                    color="light"
                    sx={{
                      ...primaryButtonSx,
                      boxShadow: "none",
                      borderColor: "rgba(255, 255, 255, 0.24)",
                      color: "#ffffff",
                    }}
                  >
                    Learn More
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox
                component="img"
                src={phoneMapImage}
                alt={`${APP_NAME} map preview`}
                sx={{
                  width: { xs: 178, sm: 220, md: 240 },
                  maxWidth: "42%",
                  minWidth: { xs: 150, md: 210 },
                  filter: "drop-shadow(0 24px 36px rgba(0, 0, 0, 0.34))",
                  borderRadius: "22px",
                }}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </BasicLayoutNew>
  );
}

export default SignIn;
